// src/utils/socialUrlParser.ts
// FULL CLEAN + ULTRA-ROBUST VERSION (especially for X/Twitter)

export type Platform = 'youtube' | 'twitter' | 'instagram' | 'soundcloud' | 'other';

export interface ParsedPost {
  platform: Platform;
  id: string;
  canonicalUrl?: string;
}

export function parseSocialUrl(urlInput: string): ParsedPost {
  const cleanUrl = urlInput.trim();
  if (!cleanUrl) {
    return { platform: 'other', id: '' };
  }

  // 1. YouTube
  const ytMatch = cleanUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))([\w-]{11})/i);
  if (ytMatch?.[1]) {
    const id = ytMatch[1];
    return {
      platform: 'youtube',
      id,
      canonicalUrl: `https://www.youtube.com/watch?v=${id}`
    };
  }

  // 2. Twitter / X — SUPER ROBUST (catches EVERY common format)
  const xRegex = /(?:https?:\/\/)?(?:www\.)?(?:x\.com|twitter\.com)\/(?:[^/\s]+|i\/web)\/status\/(\d{10,})/i;
  const xMatch = cleanUrl.match(xRegex);
  if (xMatch?.[1]) {
    const id = xMatch[1];
    return {
      platform: 'twitter',
      id,
      canonicalUrl: `https://x.com/i/status/${id}`
    };
  }

  // Extra fallback for /i/status/ links
  const xSimpleMatch = cleanUrl.match(/(?:x\.com|twitter\.com)\/i\/status\/(\d{10,})/i);
  if (xSimpleMatch?.[1]) {
    const id = xSimpleMatch[1];
    return {
      platform: 'twitter',
      id,
      canonicalUrl: `https://x.com/i/status/${id}`
    };
  }

  // 3. Instagram
  const igMatch = cleanUrl.match(/instagram\.com\/(?:p|reel|tv|stories)\/([a-zA-Z0-9_-]+)/i);
  if (igMatch?.[1]) {
    const id = igMatch[1];
    return {
      platform: 'instagram',
      id,
      canonicalUrl: `https://www.instagram.com/p/${id}/`
    };
  }

  // 4. SoundCloud (tracks, sets, on.soundcloud short links)
  const scMatch = cleanUrl.match(/(?:https?:\/\/)?(?:www\.)?(soundcloud\.com|on\.soundcloud\.com)\/([^\s?#]+)/i);
  if (scMatch?.[1] && scMatch?.[2]) {
    const host = scMatch[1]; // preserve soundcloud.com or on.soundcloud.com
    const path = scMatch[2].replace(/\/$/, '');
    const safeId = path.replace(/\//g, '--').replace(/[^a-zA-Z0-9_-]/g, '_');
    // Keep the original host form for embeds (short links work well in the widget)
    const canonicalUrl = `https://${host}/${path}`;
    return {
      platform: 'soundcloud',
      id: safeId,
      canonicalUrl
    };
  }

  // 5. Other
  try {
    const urlObj = new URL(cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`);
    const baseUrl = (urlObj.origin + urlObj.pathname).toLowerCase().replace(/\/$/, '');
    const safeId = btoa(baseUrl).replace(/[/+=]/g, '_');
    return { platform: 'other', id: safeId };
  } catch {
    const safeId = btoa(cleanUrl.toLowerCase()).replace(/[/+=]/g, '_');
    return { platform: 'other', id: safeId };
  }
}

export interface SocialMetadata {
  title?: string;
  /** Full post / tweet body text (preferred for search when title is generic) */
  description?: string;
  thumbnail?: string;
  author?: string;
}

export type YouTubeMetadata = SocialMetadata;

/** Decode common HTML entities and strip tags to plain text. */
export function htmlToPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Pull the tweet body out of Twitter/X oEmbed HTML.
 * oEmbed typically wraps the post text in a <p>…</p> inside a blockquote.
 */
export function extractTweetBodyFromOembedHtml(html: string): string | undefined {
  if (!html) return undefined;

  const pMatch = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  if (pMatch?.[1]) {
    const text = htmlToPlainText(pMatch[1]);
    if (text) return text;
  }

  let text = htmlToPlainText(html);
  text = text.replace(/\s*[—–-]\s+.+$/u, '').trim();
  return text || undefined;
}

/** True when a stored title is too generic to search usefully. */
export function isGenericPostTitle(title?: string | null): boolean {
  if (!title) return true;
  const t = title.trim();
  if (!t || t === 'Shared Content') return true;
  if (/^Post by\s+/i.test(t)) return true;
  return false;
}

const MICROLINK_CIRCUIT_KEY = 'usavisible:microlink-circuit-open';

function isMicrolinkCircuitOpen(): boolean {
  try {
    return sessionStorage.getItem(MICROLINK_CIRCUIT_KEY) === '1';
  } catch {
    return false;
  }
}

function openMicrolinkCircuit(): void {
  try {
    sessionStorage.setItem(MICROLINK_CIRCUIT_KEY, '1');
  } catch {
    /* private mode */
  }
}

async function fetchMicrolinkMetadata(shareLink: string): Promise<SocialMetadata> {
  if (!shareLink || isMicrolinkCircuitOpen()) return {};
  try {
    const res = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(shareLink)}`);
    if (res.status === 429) {
      openMicrolinkCircuit();
      return {};
    }
    if (!res.ok) return {};
    const data = await res.json();
    if (data?.status !== 'success' || !data?.data) return {};
    return {
      title: typeof data.data.title === 'string' ? data.data.title : undefined,
      description:
        typeof data.data.description === 'string' ? data.data.description.trim() : undefined,
      thumbnail: data.data.image?.url || undefined,
      author: typeof data.data.author === 'string' ? data.data.author : undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Fetch YouTube video title and thumbnail using a CORS-friendly oEmbed proxy (noembed.com).
 * No API key required. Works for youtu.be and youtube.com links.
 * Falls back gracefully on error.
 */
export async function fetchYouTubeMetadata(shareLink: string): Promise<YouTubeMetadata> {
  if (!shareLink) return {};

  // Normalize to a reliable watch URL
  const ytMatch = shareLink.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))([\w-]{11})/i);
  if (!ytMatch?.[1]) {
    return {};
  }

  const videoId = ytMatch[1];
  // Use noembed.com which adds CORS headers automatically
  const oembedUrl = `https://noembed.com/embed?url=${encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`)}`;

  try {
    const response = await fetch(oembedUrl);

    if (!response.ok) {
      throw new Error(`oEmbed HTTP ${response.status}`);
    }

    const data = await response.json();

    // noembed sometimes returns { error: "..." } if the video is private or doesn't exist
    if (data.error) {
      console.warn('noembed returned error:', data.error);
      return {};
    }

    return {
      title: data.title || undefined,
      thumbnail: data.thumbnail_url || undefined,
      author: data.author_name || undefined,
    };
  } catch (error) {
    console.warn('YouTube metadata fetch failed for', shareLink, error);
    return {};
  }
}

/**
 * Fetch Twitter / X metadata using publish.twitter.com oEmbed (body text),
 * FixTweet for media posters, and optional Microlink (fallback title/thumb).
 */
export async function fetchTwitterMetadata(
  shareLink: string,
  options: { includeMicrolink?: boolean } = {}
): Promise<SocialMetadata> {
  if (!shareLink) return {};
  const { includeMicrolink = true } = options;
  const result: SocialMetadata = {};

  const statusId =
    shareLink.match(/(?:x\.com|twitter\.com)\/(?:[^/\s]+\/)?status\/(\d{10,})/i)?.[1] ??
    shareLink.match(/status\/(\d{10,})/i)?.[1] ??
    null;
  const normalizedUrl = statusId
    ? `https://twitter.com/i/status/${statusId}`
    : shareLink.replace(/x\.com/i, 'twitter.com');
  const oembedUrl = `https://publish.twitter.com/oembed?omit_script=true&url=${encodeURIComponent(normalizedUrl)}`;

  try {
    const response = await fetch(oembedUrl);
    if (response.ok) {
      const data = await response.json();
      const body = extractTweetBodyFromOembedHtml(data.html || '');
      if (body) {
        result.description = body;
        result.title = body.length > 199 ? `${body.slice(0, 196)}...` : body;
      }
      if (data.author_name) {
        result.author = data.author_name;
        if (!result.title) result.title = `Post by ${data.author_name}`;
      }
    }
  } catch {
    /* oEmbed blocked or offline */
  }

  // Media poster via FixTweet (oEmbed never includes images; Microlink rate-limits)
  if (statusId && !result.thumbnail) {
    try {
      const { fetchTweetPoster } = await import('./platformEmbeds');
      const poster = await fetchTweetPoster(statusId);
      if (poster) result.thumbnail = poster;
    } catch {
      /* offline / blocked */
    }
  }

  if (includeMicrolink && (!result.description || !result.thumbnail || isGenericPostTitle(result.title))) {
    const micro = await fetchMicrolinkMetadata(shareLink);
    if (!result.thumbnail && micro.thumbnail) result.thumbnail = micro.thumbnail;
    if (!result.author && micro.author) result.author = micro.author;
    if (!result.description && micro.description) result.description = micro.description;
    if (isGenericPostTitle(result.title)) {
      if (micro.description) {
        result.title =
          micro.description.length > 199
            ? `${micro.description.slice(0, 196)}...`
            : micro.description;
      } else if (micro.title) {
        result.title = micro.title;
      } else if (result.author) {
        result.title = `Post by ${result.author}`;
      }
    }
  }

  return result;
}

/**
 * Fetch Instagram metadata (thumbnail) using a best-effort public media URL pattern.
 * Works for many public posts and reels without an API key.
 * Falls back gracefully if the constructed URL is not directly usable.
 */
export async function fetchInstagramMetadata(shareLink: string): Promise<SocialMetadata> {
  if (!shareLink) return {};

  try {
    // Attempt Microlink for Instagram to fetch clean, public, unblocked proxy thumbnail
    const res = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(shareLink)}`);
    if (res.ok) {
      const data = await res.json();
      if (data?.status === 'success' && data?.data) {
        return {
          title: data.data.title || undefined,
          thumbnail: data.data.image?.url || undefined,
          author: data.data.author || undefined,
        };
      }
    }
  } catch {
    console.warn('Microlink fetch for Instagram metadata failed, using fallback...');
  }

  const match = shareLink.match(/instagram\.com\/(?:p|reel|tv|stories)\/([a-zA-Z0-9_-]+)/i);
  if (!match?.[1]) return {};
  const shortcode = match[1];
  // Fallback to standard Instagram media thumbnail URL
  const thumbUrl = `https://www.instagram.com/p/${shortcode}/media/?size=l`;
  return {
    thumbnail: thumbUrl,
  };
}

/**
 * Fetch SoundCloud metadata using Microlink (primary) + official SoundCloud oEmbed fallback.
 */
export async function fetchSoundCloudMetadata(shareLink: string): Promise<SocialMetadata> {
  if (!shareLink) return {};

  try {
    const res = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(shareLink)}`);
    if (res.ok) {
      const data = await res.json();
      if (data?.status === 'success' && data?.data) {
        return {
          title: data.data.title || undefined,
          thumbnail: data.data.image?.url || undefined,
          author: data.data.author || undefined,
        };
      }
    }
  } catch {
    console.warn('Microlink fetch for SoundCloud metadata failed, falling back to oEmbed...');
  }

  // Official SoundCloud oEmbed
  try {
    const oembedUrl = `https://soundcloud.com/oembed?url=${encodeURIComponent(shareLink)}&format=json`;
    const response = await fetch(oembedUrl);
    if (!response.ok) {
      throw new Error(`SoundCloud oEmbed HTTP ${response.status}`);
    }
    const data = await response.json();
    return {
      title: data.title || undefined,
      thumbnail: data.thumbnail_url || undefined,
      author: data.author_name || undefined,
    };
  } catch (error) {
    console.warn('SoundCloud metadata fetch failed for', shareLink, error);
    return {};
  }
}